import React from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import * as Styles from './FastCheckoutCart.styles';
import {
  Accordion, Button, IconInfo, IconSecurityV2, Tag,
  Tooltip,
} from 'gatorcomponents';
import { DISCOUNT_TYPES, FAST_CHECKOUT_PAYMENT_METHODS } from '../../FastCheckout.enum';
import { FastCheckoutCC } from '../../PaymentMethods';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';

const FastCheckoutCart = ({
  paymentMethod,
  onFinalizePurchase,
  renderIframe,
  productCycle,
  currentCreditCard,
  isCcButtonLoading,
  isCcButtonEnabled,
  isMobile,
  creditCardMethod,
  setCreditCardMethod,
  fastCheckoutLocale,
  product,
  hasDiscount,
}) => {
  const handleOnFinalizePurchase = () => {
    onFinalizePurchase && onFinalizePurchase();
  };

  const {
    productName, productIcon, productIsTrial, productDiscount, productBasePrice, productFinalPrice, productRenewalDate, productVat,
  } = product;

  const { title: toolTipTitle, description: toolTipDescription, topics: toolTipTopics } = fastCheckoutLocale.taxToolTip;

  const finalizeBlock = (
    <>
      <Styles.ButtonWrapper>
        <Button disabled={!isCcButtonEnabled} loading={isCcButtonLoading} onClick={handleOnFinalizePurchase} width="full" size="large" label={fastCheckoutLocale.ctaPurchase} />
      </Styles.ButtonWrapper>
      <Styles.SecurityReminderWrapper>
        <IconSecurityV2 size="sm" />
        <Styles.SecurityReminder>{fastCheckoutLocale.securityReminder}</Styles.SecurityReminder>
      </Styles.SecurityReminderWrapper>
    </>
  );

  const taxToolTipContent = (
    <Styles.TaxToolTipWrapper>
      <Styles.TaxToolTipTitle>
        {toolTipTitle}
      </Styles.TaxToolTipTitle>
      <Styles.TaxToolTipContent>
        {toolTipDescription}
      </Styles.TaxToolTipContent>
      <Styles.TaxToolTipList>
        {toolTipTopics.map(topic => (
          <Styles.TaxToolTipListItem key={topic.id}>
            {topic.text}
          </Styles.TaxToolTipListItem>
        ))}
      </Styles.TaxToolTipList>
    </Styles.TaxToolTipWrapper>
  );

  const paymentMethodInfo = () => {
    switch (paymentMethod) {
      case FAST_CHECKOUT_PAYMENT_METHODS.CREDIT_CARD:
        return (
          <FastCheckoutCC
            renewalDate={productRenewalDate}
            locale={fastCheckoutLocale.paymentMethods[FAST_CHECKOUT_PAYMENT_METHODS.CREDIT_CARD]}
            iframe={renderIframe}
            creditCardMethod={creditCardMethod}
            setCreditCardMethod={setCreditCardMethod}
            currentCreditCard={currentCreditCard}
          />
        );
      case FAST_CHECKOUT_PAYMENT_METHODS.PIX:
        // Note: Temporary, dont have figma
        return <h2>Pix</h2>;
      case FAST_CHECKOUT_PAYMENT_METHODS.GP_BOLETO:
        // Note: Temporary, dont have figma
        return <h2>Boleto</h2>;
      default:
        return null;
    }
  };

  const paymentAccordionInfo = () => (
    <Styles.PaymentAccordionWrapper>
      {(paymentMethod && fastCheckoutLocale.paymentMethods[paymentMethod]) && (
        <Styles.PaymentInformation>{paymentMethod && fastCheckoutLocale.paymentMethods[paymentMethod].label}</Styles.PaymentInformation>
      )}
      {paymentMethodInfo()}
      {isMobile && finalizeBlock}
      <Styles.RenewalInformation>
        {productIsTrial ? fastCheckoutLocale.renewalSamePaymentMethodInTrial : fastCheckoutLocale.renewalSamePaymentMethod}
      </Styles.RenewalInformation>
    </Styles.PaymentAccordionWrapper>
  );

  return (
    <Styles.Content>
      <Styles.Cart>
        {!isMobile && (
          <Styles.CartTitle>
            {fastCheckoutLocale.cartTitle}
          </Styles.CartTitle>
        )}
        <Styles.AccordionWrapper>
          <Accordion titleSize="small" open items={paymentAccordionInfo} chevronColor="primaryMedium" withShadow={false} withBorder width="full" leftIcon="IconCard" iconPositionStatic title={fastCheckoutLocale.paymentTitle} />
        </Styles.AccordionWrapper>
      </Styles.Cart>
      <Styles.Resume>
        {isMobile && (
          <Styles.CartTitle>
            {fastCheckoutLocale.cartTitle}
          </Styles.CartTitle>
        )}
        <Styles.ResumeTitle>
          {fastCheckoutLocale.resumeTitle}
        </Styles.ResumeTitle>
        <Styles.ProductDetailsBox>
          <Styles.ProductTitle>{fastCheckoutLocale.product}</Styles.ProductTitle>
          <Styles.TagWrapper>
            <Tag variant="info" label={fastCheckoutLocale.cycles[productCycle]} />
          </Styles.TagWrapper>
          <Styles.ProductNameWrapper>
            <Styles.ProductName>{productName}</Styles.ProductName>
            {productIcon && <Styles.ProductIcon src={productIcon} />}
          </Styles.ProductNameWrapper>
          {hasDiscount && (
            <Styles.BasePriceWrapper>
              <Tag variant="success" label={`${fastCheckoutLocale.discountTag} ${productDiscount.type === DISCOUNT_TYPES.PERCENTAGE ? productDiscount.percentage : formatCurrency(productDiscount.value)}`} />
              <Styles.ProductBasePriceWrapper>
                <Styles.ProductBasePrice>
                  {fastCheckoutLocale.value}
                </Styles.ProductBasePrice>
                <Styles.ProductBasePrice>
                  {formatCurrency(productBasePrice)}
                </Styles.ProductBasePrice>
              </Styles.ProductBasePriceWrapper>
            </Styles.BasePriceWrapper>
          )}
          {productVat.length > 0 && (
            <Styles.TaxesWrapper>
              {productVat.map(vat => (
                <Styles.ProductTaxWrapper key={vat.name}>
                  <Styles.ProductTax>
                    {vat.name}
                    <Styles.IconInfoWrapper>
                      <Tooltip content={taxToolTipContent} preferredPosition={isMobile ? 'bottomRight' : 'bottomLeft'}>
                        <IconInfo size="sm" color="primaryMedium" />
                      </Tooltip>
                    </Styles.IconInfoWrapper>
                  </Styles.ProductTax>
                  <Styles.ProductTax>
                    {formatCurrency(vat.value)}
                  </Styles.ProductTax>
                </Styles.ProductTaxWrapper>
              ))}
            </Styles.TaxesWrapper>
          )}
          <Styles.ProductFinalPriceWrapper>
            <Styles.ProductFinalPrice>{fastCheckoutLocale.total}</Styles.ProductFinalPrice>
            <Styles.ProductFinalPrice>{formatCurrency(productFinalPrice)}</Styles.ProductFinalPrice>
          </Styles.ProductFinalPriceWrapper>
          <Styles.ProductRenewal>
            {`${fastCheckoutLocale.renewal} ${productRenewalDate}`}
          </Styles.ProductRenewal>
        </Styles.ProductDetailsBox>
        {!isMobile && finalizeBlock}
      </Styles.Resume>
    </Styles.Content>
  );
};
export default withErrorBoundary(FastCheckoutCart, 'FastCheckoutCart');
